import {
  AppSessionAtomicOperation,
  AppSessionCellId,
  CREATE_EXPLORE_SPEC_SESSION_STATE,
  ExploreCellId,
  ExploreFieldOverrides,
  ExploreSpec,
  ExploreSpecSessionStateId,
  ExploreSpecSessionStateUpdatableFields,
  ExploreSpecUpdateActions,
  UPDATE_EXPLORE_SPEC_SESSION_STATE,
  exploreSpecActions,
  isPivotHqlSpecsChanged,
  mergeExploreSpecWithSessionState,
  uuid,
} from "@hex/common";
import { isEqual } from "lodash";

import { ExploreSpecSessionStateMP } from "../../redux/slices/appSessionMPSlice.js";

/**
 * Converts ExploreSpec actions directly to ExploreSpec session state operations
 */
export function getUpdateExploreSpecSessionStateAOs({
  actions,
  appSessionCellId,
  currentSpec,
  exploreCellId,
  getExploreSpecSessionState,
}: {
  actions: ExploreSpecUpdateActions | ExploreSpecUpdateActions[];
  appSessionCellId: AppSessionCellId;
  exploreCellId: ExploreCellId;
  currentSpec: ExploreSpec;
  getExploreSpecSessionState: (
    appSessionCellId: AppSessionCellId,
  ) => ExploreSpecSessionStateMP | undefined;
}): AppSessionAtomicOperation[] {
  const actionsArray = Array.isArray(actions) ? actions : [actions];
  const currentSessionState = getExploreSpecSessionState(appSessionCellId);

  let accumulatedSessionState: ExploreSpecSessionStateUpdatableFields = {
    fieldOverrides: currentSessionState?.fieldOverrides || null,
  };

  // We apply each action sequentially to build up to one final session state
  // This avoids the need to call multiple AOs to update the session state
  for (const action of actionsArray) {
    accumulatedSessionState = applyActionToSessionState(
      action,
      accumulatedSessionState,
    );
  }
  return createSessionStateUpdateOps({
    currentSessionState,
    newSessionState: accumulatedSessionState,
    appSessionCellId,
    exploreCellId,
    currentSpec,
  });
}

/**
 * Applies a single ExploreSpec action to session state, returning an updated session state
 */
function applyActionToSessionState(
  action: ExploreSpecUpdateActions,
  sessionState: ExploreSpecSessionStateUpdatableFields,
): ExploreSpecSessionStateUpdatableFields {
  switch (action.type) {
    case "exploreSpec/setFieldProperty":
      return applySetFieldPropertyToSessionState(action, sessionState);
    default:
      // Unsupported action types return unchanged session state, but do not throw
      console.error(
        `ExploreSpecSessionStateUpdate encountered an unsupported action type: ${action.type}`,
      );
      return sessionState;
  }
}

/**
 * Applies the setFieldProperty exploreSpecAction to an ExploreSpecSessionState
 */
function applySetFieldPropertyToSessionState(
  action: ReturnType<typeof exploreSpecActions.setFieldProperty>,
  sessionState: ExploreSpecSessionStateUpdatableFields,
): ExploreSpecSessionStateUpdatableFields {
  const { fieldId, updateData } = action.payload;

  // Only handle properties that are supported in ExploreFieldOverride
  // Currently only supports sort -- in the future if additional props are added, we should make this more robust
  if (!updateData.sort) {
    return sessionState;
  }

  const updatedFieldOverrides: ExploreFieldOverrides = {
    ...sessionState.fieldOverrides,
    [fieldId]: {
      sort: updateData.sort,
    },
  };

  return {
    ...sessionState,
    fieldOverrides: updatedFieldOverrides,
  };
}

/**
 * Generates atomic operations for changes to ExploreSpecSessionStates
 */
function createSessionStateUpdateOps({
  appSessionCellId,
  currentSessionState,
  currentSpec,
  exploreCellId,
  newSessionState,
}: {
  appSessionCellId: AppSessionCellId;
  currentSpec: ExploreSpec;
  exploreCellId: ExploreCellId;
  currentSessionState: ExploreSpecSessionStateMP | undefined;
  newSessionState: ExploreSpecSessionStateUpdatableFields;
}): AppSessionAtomicOperation[] {
  const ops: AppSessionAtomicOperation[] = [];

  const mergedSpec = mergeExploreSpecWithSessionState(
    currentSpec,
    newSessionState,
  );
  const shouldRunCell = isPivotHqlSpecsChanged(currentSpec, mergedSpec);

  // If the session state does not exist, we need to create it
  if (!currentSessionState) {
    ops.push(
      CREATE_EXPLORE_SPEC_SESSION_STATE.create({
        appSessionCellId,
        exploreCellId,
        exploreSpecSessionStateId: uuid() as ExploreSpecSessionStateId,
        options: newSessionState,
        shouldRunCell,
      }),
    );
  } else {
    if (
      !isEqual(
        currentSessionState.fieldOverrides,
        newSessionState.fieldOverrides,
      )
    ) {
      ops.push(
        UPDATE_EXPLORE_SPEC_SESSION_STATE.create({
          exploreSpecSessionStateId: currentSessionState.id,
          // We currently only support field overrides, but in the future if additional props are added, we should make this more robust
          key: "fieldOverrides",
          value: newSessionState.fieldOverrides,
          shouldRunCell,
        }),
      );
    }
  }
  return ops;
}
